/**
 * PAGE: LISTE RÉDUCTIONS (VERSION REFACTORISÉE)
 */

import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import {
  ColumnSelectorModal,
  CreateDiscountModal,
  FiltersModal,
  ImportCSVModal,
} from '../components/modals';
import { ActionButtons, PageContent, PageHeader, PageLayout, Pagination } from '../components/page';
import { EnterpriseTable } from '../components/tables';
import { CheckboxCell, ActionsCell } from '../components/tables/cells';
import { TableCell } from '../components/table';
import {
  BulkActionsBar,
  ConfirmDialog,
  ErrorState,
  LoadingState,
  toast,
  Select,
} from '../components/ui';
import { useEntityList } from '../hooks/useEntityList';
import { useDiscounts } from '../hooks/useDiscounts';
import { useSupabase } from '../hooks/useSupabase';
import { usePermissions } from '../hooks/usePermissions';
import type { Discount } from '../types/discount';
import { DISCOUNT_CONFIG } from '../config/entities/discount.config';
import {
  generateCSVColumns,
  generateCSVTransformers,
  generateImportConfig,
  generateFilterConfigs,
  generateColumnOptions,
} from '../lib/entity-config';

export function DiscountsListPage() {
  const supabase = useSupabase();
  const {
    data: discounts,
    loading,
    error,
    create,
    update,
    remove,
    removeMany,
    refreshDiscounts,
  } = useDiscounts();

  // Permissions utilisateur
  const {
    canCreateEntity,
    canUpdateEntity,
    canDeleteEntity,
    canImportData,
    canExportData,
    canDeleteBulk,
  } = usePermissions();

  // Tout est généré automatiquement depuis DISCOUNT_CONFIG
  const filterConfigs = useMemo(() => generateFilterConfigs(DISCOUNT_CONFIG), []);
  const csvColumns = useMemo(() => generateCSVColumns(DISCOUNT_CONFIG), []);
  const csvTransformers = useMemo(() => generateCSVTransformers(DISCOUNT_CONFIG), []);

  const importConfig = useMemo(() => generateImportConfig(DISCOUNT_CONFIG), []);

  const entity = useEntityList({
    data: discounts,
    loading,
    error,
    create,
    remove,
    removeMany,
    refresh: refreshDiscounts,
    entityName: DISCOUNT_CONFIG.entityName,
    entityNamePlural: DISCOUNT_CONFIG.entityNamePlural,
    searchableFields: ['name'] as (keyof Discount)[],
    filterConfigs,
    csvFileName: 'reductions.csv',
    csvColumns,
    csvTransformers,
    importConfig,
  });

  const { handleDeleteClick } = entity;

  const handleStatusChange = useCallback(
    async (id: string, newStatus: string) => {
      if (!update) return;
      const success = await update(id, { status: newStatus });
      if (success) {
        toast.success('Statut mis à jour');
      } else {
        toast.error('Erreur lors de la mise à jour du statut');
      }
    },
    [update]
  );

  // Vérifier si des actions sont possibles
  const hasAnyBulkAction = canDeleteBulk('discount') || canExportData('discount');
  const hasAnyRowAction = canDeleteEntity('discount');

  const columns: ColumnDef<Discount>[] = useMemo(
    () => [
      // Colonne checkbox uniquement si actions de masse possibles
      ...(hasAnyBulkAction
        ? ([
            {
              id: 'select',
              size: 60,
              header: ({ table }: any) => (
                <CheckboxCell
                  row={
                    {
                      getIsSelected: () => table.getIsAllRowsSelected(),
                      toggleSelected: () => table.toggleAllRowsSelected(),
                    } as any
                  }
                />
              ),
              cell: ({ row }: any) => <CheckboxCell row={row} />,
            },
          ] as ColumnDef<Discount>[])
        : []),
      {
        accessorKey: 'name',
        header: 'Nom',
        cell: ({ getValue }) => (
          <TableCell className="font-semibold">{getValue() as string}</TableCell>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'amount',
        header: 'Montant',
        cell: ({ getValue }) => {
          const amount = getValue() as number;
          return <TableCell>{amount.toLocaleString('fr-FR') + ' €'}</TableCell>;
        },
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: 'Statut',
        cell: ({ row, getValue }) => {
          const status = getValue() as string;
          return (
            <Select
              variant="inline"
              value={status}
              onChange={(e) => handleStatusChange(row.original.id, e.target.value)}
              disabled={!canUpdateEntity('discount')}
              className="w-full"
              options={[
                { value: 'Active', label: 'Active' },
                { value: 'Inactive', label: 'Inactive' },
              ]}
            />
          );
        },
        enableSorting: true,
      },
      // Colonne actions uniquement si des actions sont possibles
      ...(hasAnyRowAction
        ? ([
            {
              id: 'actions',
              header: 'Action',
              cell: ({ row }: any) => (
                <ActionsCell
                  onDelete={() => handleDeleteClick(row.original.id, row.original.name)}
                  canDelete={canDeleteEntity('discount')}
                />
              ),
              size: 100,
            },
          ] as ColumnDef<Discount>[])
        : []),
    ],
    [
      handleStatusChange,
      handleDeleteClick,
      canDeleteEntity,
      canUpdateEntity,
      hasAnyBulkAction,
      hasAnyRowAction,
    ]
  );

  // Colonnes disponibles pour la sélection (générées automatiquement)
  const columnOptions = useMemo(
    () => generateColumnOptions(DISCOUNT_CONFIG, entity.table.columnVisibility),
    [entity.table.columnVisibility]
  );

  if (entity.loading) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Réductions' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <LoadingState message="Chargement des réductions..." />
      </PageLayout>
    );
  }

  if (entity.error) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Réductions' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <ErrorState
          title="Erreur lors du chargement des réductions"
          message={entity.error.message}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Réductions' }]} />
      <div className="w-full h-px bg-zinc-200" />

      <PageContent
        title="Réductions"
        description="Gérez les réductions et remises"
        actions={
          <ActionButtons
            searchValue={entity.table.searchTerm}
            onSearchChange={entity.table.setSearchTerm}
            onDownload={canExportData('discount') ? entity.handleExportCSV : undefined}
            onUpload={
              canImportData('discount') ? () => entity.setIsImportModalOpen(true) : undefined
            }
            onFilter={() => entity.setIsFiltersModalOpen(true)}
            onColumns={() => entity.setIsColumnModalOpen(true)}
            onCreate={
              canCreateEntity('discount') ? () => entity.setIsCreateModalOpen(true) : undefined
            }
            activeFiltersCount={entity.table.activeFilters.length}
          />
        }
        footer={
          <Pagination
            currentPage={entity.table.currentPage}
            totalPages={entity.table.totalPages}
            itemsPerPage={entity.table.itemsPerPage}
            totalItems={entity.table.totalItems}
            displayedItems={entity.table.displayedItemsCount}
            onPageChange={entity.table.setCurrentPage}
            onItemsPerPageChange={entity.table.setItemsPerPage}
            itemsPerPageOptions={[10, 25, 50, 100]}
          />
        }
      >
        <EnterpriseTable
          data={entity.table.filteredData}
          columns={columns}
          onRowSelectionChange={entity.table.setSelectedItems}
          columnVisibility={entity.table.columnVisibility}
          onColumnVisibilityChange={entity.table.setColumnVisibility}
          clearSelectionTrigger={entity.table.clearSelectionTrigger}
          pageSize={entity.table.itemsPerPage}
          currentPage={entity.table.currentPage}
          onPageChange={entity.table.setCurrentPage}
        />
      </PageContent>

      <ColumnSelectorModal
        isOpen={entity.isColumnModalOpen}
        onClose={() => entity.setIsColumnModalOpen(false)}
        columns={columnOptions}
        onApply={(newColumns) => {
          const newVisibility: Record<string, boolean> = {};
          newColumns.forEach((col) => {
            newVisibility[col.id] = col.visible !== false;
          });
          entity.table.setColumnVisibility(newVisibility);
        }}
      />

      <FiltersModal
        isOpen={entity.isFiltersModalOpen}
        onClose={() => entity.setIsFiltersModalOpen(false)}
        filterConfigs={entity.filterConfigs}
        activeFilters={entity.table.activeFilters}
        activeFilterMode={entity.table.filterMode}
        onApply={entity.table.setActiveFilters}
      />

      {/* ✅ OPTIMISATION: Monter la modale uniquement quand elle est ouverte */}
      {entity.isCreateModalOpen && (
        <CreateDiscountModal
          isOpen={entity.isCreateModalOpen}
          onClose={() => entity.setIsCreateModalOpen(false)}
          onSubmit={async (data) => {
            await entity.handleCreate(data);
          }}
        />
      )}

      <ConfirmDialog
        open={entity.deleteConfirm.open}
        onOpenChange={(open) => entity.setDeleteConfirm({ open })}
        title={
          entity.deleteConfirm.isBulk
            ? `Supprimer les ${DISCOUNT_CONFIG.entityNamePlural}`
            : `Supprimer la ${DISCOUNT_CONFIG.entityName}`
        }
        description={
          entity.deleteConfirm.isBulk
            ? `Êtes-vous sûr de vouloir supprimer ${entity.table.selectedItems.length} ${DISCOUNT_CONFIG.entityName}(s) ?`
            : `Êtes-vous sûr de vouloir supprimer "${entity.deleteConfirm.name}" ?`
        }
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={entity.confirmDelete}
      />

      <ImportCSVModal
        open={entity.isImportModalOpen}
        onOpenChange={entity.setIsImportModalOpen}
        config={importConfig}
        mode={entity.importMode}
        onCreate={async (data) => {
          const { data: discount, error: supabaseError } = await supabase
            .from('discounts')
            .insert([data as any])
            .select()
            .single();

          if (supabaseError) {
            throw supabaseError;
          }

          return discount;
        }}
        onUpdate={update}
        existingData={discounts}
        entityName={DISCOUNT_CONFIG.entityName}
        entityNamePlural={DISCOUNT_CONFIG.entityNamePlural}
        onSuccess={entity.handleImportSuccess}
      />

      {/* Barre d'actions de masse */}
      {entity.table.selectedItems.length > 0 && (
        <BulkActionsBar
          count={entity.table.selectedItems.length}
          entityNamePlural={DISCOUNT_CONFIG.entityNamePlural}
          onDelete={entity.handleBulkDeleteClick}
          onExportCSV={entity.handleExportSelectedCSV}
          onClearSelection={entity.table.clearSelection}
          showEmailAction={false}
          canDelete={canDeleteBulk('discount')}
          canExport={canExportData('discount')}
        />
      )}
    </PageLayout>
  );
}
